<template>
  <b-button
    pill
    variant="primary"
    :disabled="modalDisabled || processing"
    @click="certify" aria-label="Certify Notification">
    Certify
    <b-modal
      ref="certifyConfirmation"
      content-class="px-lg-5"
      id="certifyConfirmation"
      title="Acknowledge and Certify"
      footer-class="text-center"
      centered
      hide-header-close
      size="lg"
    >
      <b-form-checkbox
        id="correctnessAcknowledgement"
        class="mb-3"
        v-model="correctnessAcknowledgement"
        :disabled="mfsAdmin"
      >
        <span class='custom-checkbox-label'>As the {{fullRolesList}}, I certify all answers to questions here are correct to the best of my knowledge and belief.</span>
      </b-form-checkbox>
      <b-form-checkbox
        v-if="mfsAdmin || roles.landowner" v-model="landownerAcknowledgement"
        id="landownerAcknowledgement"
        class="mb-3"
        :disabled="mfsAdmin"
      >
        <div class='custom-checkbox-label'>
          <span>As the Landowner, I understand that if I named a Designated Agent that I have given them responsibility for all operations described in this notification. </span>
        </div>
      </b-form-checkbox>
      <b-form-checkbox
        v-if="mfsAdmin || roles.landowner" v-model="landownerReportAcknowledgement"
        id="landownerReportAcknowledgement"
        class="mb-3"
        :disabled="mfsAdmin"
      >
        <div class='custom-checkbox-label'>
          <span>I understand that, as the landowner, I am legally responsible for the completion of the year-end landowner reports that are <strong><i>due annually on January 31st</i></strong> if harvest activities are included in this Forest Operation Notification.</span>
        </div>
      </b-form-checkbox>
      <b-form-checkbox
        id="daAcknowledgement"
        class="mb-3"
        v-if="mfsAdmin || roles.designatedAgent"
        v-model="daAcknowledgement"
        :disabled="mfsAdmin"
      >
        <div v-if="notification.certificationMethodId === 2 || notification.certificationMethodId === 3" class='custom-checkbox-label'>
          As the {{agentPlusOptionalForester}}, I certify that I am authorized by the Landowner to certify this FON on their behalf.
        </div>
        <template v-else>
          <div v-if="notification.certificationMethodId === 1" class='custom-checkbox-label'>
            <span>As the {{agentPlusOptionalForester}}, I understand that after I submit this FON, the Landowner must sign in to FOResT and certify that I am their Designated Agent and have been given responsibility for all operations described on this FON.</span>
          </div>
          <div v-else class='custom-checkbox-label'>
            <span v-if="!roles.licensedForester">As the Landowner's Designated Agent, I certify that I am authorized by the Landowner to certify this FON on their behalf. </span>
            <span v-else>As the Landowner's Designated Agent and a Maine Licensed Forester, I certify that I am authorized by the Landowner to certify this FON on their behalf. </span>
            <span>I acknowledge that my responsibilities, fiduciary and/or otherwise, to the Landowner are governed by legal requirements and Maine's Code of Ethics for Licensed Foresters.</span>
          </div>
        </template>
      </b-form-checkbox>
      <b-form-checkbox
        id="submissionAcknowledgement"
        v-model="submissionAcknowledgement"
        :disabled="mfsAdmin">
        <div class='custom-checkbox-label'>
          <span>I understand that this FON will not be submitted to the Maine Forest Service until all parties listed as contacts have reviewed and certified acceptance. </span>
          <span>Once all parties have certified, a Notification Number will be issued and the FON will be automatically submitted to MFS. </span>
          <span>I also understand that the activity on this notification cannot begin until all permitting and other documentation requirements identified in the Important Reminders section are completed and approved.</span>
        </div>
      </b-form-checkbox>
      <div class="certify-disclaimer">
        <strong><em>
          Warning: once you have selected “Acknowledge and Certify this information”
          you might not be able to make further changes.
        </em></strong>
      </div>
      <div slot="modal-footer" class="d-flex">
        <b-button
          pill
          variant="danger"
          class="mr-3"
          :disabled="processing"
          @click="hideModal" aria-label="Cancel certifying this Notification">
          Cancel
        </b-button>
        <b-button
          pill
          variant="primary"
          :disabled="mfsAdmin || !acknowledged || processing"
          @click="certified()" aria-label="Acknowledge and Certify this Notification">
          Acknowledge and Certify
        </b-button>
      </div>
    </b-modal>
  </b-button>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Utilities from '@/mixins/Utilities';

const initialState = () => ({
  correctnessAcknowledgement: false,
  landownerAcknowledgement: false,
  landownerReportAcknowledgement: false,
  daAcknowledgement: false,
  submissionAcknowledgement: false,
  // processing is used to disable the certify buttons while the api call is doing its thing
  processing: false,
});

export default {
  name: 'CertifyModal',
  mixins: [Utilities],
  props: {
    modalDisabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return initialState();
  },
  computed: {
    ...mapState('Notification', {
      notification: state => state.notification,
      roles: state => state.roles,
    }),
    ...mapGetters('Notification', ['pendingCertification']),
    ...mapGetters('User', ['mfsAdmin']),
    fullRolesList() {
      const roleStrings = [];
      if (this.roles.landowner) roleStrings.push('Landowner');
      if (this.roles.designatedAgent) roleStrings.push('Designated Agent');
      if (this.roles.operator) roleStrings.push('Harvester/Operator');
      if (this.roles.licensedForester) roleStrings.push('Licensed Forester');
      if (roleStrings.length === 0) {
        roleStrings.push('<User\'s Role>');
      }
      return this.joinList(roleStrings);
    },
    agentPlusOptionalForester() {
      return this.roles.licensedForester ? "Landowner's Designated Agent and Licensed Forester" : "Landowner's Designated Agent"
    },
    acknowledged() {
      return this.correctnessAcknowledgement
        && this.submissionAcknowledgement
        && (!this.roles.designatedAgent || this.daAcknowledgement)
        && (!this.roles.landowner || this.landownerAcknowledgement)
        && (!this.roles.landowner || this.landownerReportAcknowledgement);
    },
  },
  methods: {
    certify() {
      Object.assign(this.$data, initialState());
      this.$refs.certifyConfirmation.show();
    },
    async certified() {
      if (this.mfsAdmin || this.processing) {
        return;
      }

      this.processing = true;

      await this.$store.dispatch(
        this.pendingCertification ? 'Notification/certify' : 'Notification/submit',
        this.notification.id,
      );
      this.$emit('certified');
      this.processing = false;
    },
    hideModal() {
      this.$refs.certifyConfirmation.hide();
    },
  },
};
</script>
